import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IIndicationStrategy, IntuitiveIndication } from '../../models';
import { TOOTH_MODAL_ACTION } from '../../models/tooth-modal';
import { INDICATION_OPERATIONS } from './indication-context.token';
import { catchError, tap } from 'rxjs/operators';

/**
 * Indications that open a modal dialog before committing the assignment inside
 * an active bridge.
 */
const BRIDGE_MODAL_INDICATIONS: ReadonlySet<IntuitiveIndication> = new Set([IntuitiveIndication.BridgeImplantBased]);

/**
 * Strategy for handling teeth diagram button clicks inside an active bridge.
 *
 * For modal indications ({@link BRIDGE_MODAL_INDICATIONS}) the appropriate modal is opened
 * first; the indication is committed only on Save. Cancel and Delete are no-ops.
 * For all other indications the assignment is applied immediately.
 *
 * The `context` parameter (a {@link Tooth} key) is accepted for interface compliance and is
 * reserved for future routing logic — it is not used by the current implementation.
 *
 * Provided in {@link RxIntuitiveTeethBridgeOrchestratorComponent} and wired to
 * {@link BridgeIndicationService} via the {@link INDICATION_OPERATIONS} token.
 */
@Injectable()
export class BridgeStrategy implements IIndicationStrategy {
	private readonly operations = inject(INDICATION_OPERATIONS);

	teethDiagramButtonHandle(value: number | string): Observable<any> {
		const indication = value as IntuitiveIndication;

		if (BRIDGE_MODAL_INDICATIONS.has(indication)) {
			return this.handleWithModal(indication);
		}

		this.operations.assignIndication(indication);

		return of(undefined);
	}

	private handleWithModal(indication: IntuitiveIndication): Observable<void> {
		const modalResult$ = this.operations.openModalForUnitTypeIfNeeded(indication);

		if (!modalResult$) {
			this.operations.assignIndication(indication);

			return of(undefined);
		}

		return new Observable<void>(observer => {
			const sub = modalResult$
				.pipe(
					tap(result => {
						if (result?.action === TOOTH_MODAL_ACTION.Cancel || result?.action === TOOTH_MODAL_ACTION.Delete) {
							observer.complete();

							return;
						}

						this.operations.assignIndication(indication, result?.action === TOOTH_MODAL_ACTION.Save);
						observer.complete();
					}),
					catchError(err => {
						observer.error(err);

						return of(undefined);
					})
				)
				.subscribe();

			return () => sub.unsubscribe();
		});
	}
}
